import axios from "axios";
import Cookies from "js-cookie";
import { LOGIN_PATH } from "./routes";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import { flattenMessage } from "./errorMessage";
import { lupakanPenandaPengumuman } from "./pengumuman";

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const JARI_EMAS = "https://jariemas-api.menlhk.go.id/";
const PUBLIC_API_TOKEN = process.env.REACT_APP_PUBLIC_TOKEN_API;
export const MAP_BOX_API = process.env.REACT_APP_MAP_BOX_API_KEY;
export const MOTIVASI_MERKURI = "https://motivasimerkuri-api.menlhk.go.id/";
export const HIGHCHART_URL = process.env.REACT_APP_HIGHCHART_URL;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const axiosInstanceAuth = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const axiosInstanceAuthPublic = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Instance khusus refresh token - SENGAJA tanpa interceptor apa pun.
 *
 * Inilah yang memutus rekursi: refreshToken() versi lama memakai
 * axiosInstanceAuth, instance yang punya interceptor 401 sendiri, dan request
 * interceptor-nya melampirkan accessToken lama yang sudah invalid. Kalau
 * endpoint refresh berada di belakang JwtAuthGuard, ia membalas 401 dan
 * interceptor terpanggil lagi tanpa henti.
 */
const axiosRefresh = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const axiosInstanceAuthJariEmas = axios.create({
  baseURL: JARI_EMAS,
  headers: {
    "Content-Type": "application/json",
  },
});

export const axiosInstanceAuthMotivasiMerkuri = axios.create({
  baseURL: MOTIVASI_MERKURI,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstanceAuth.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${Cookies.get("accessToken")}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstanceAuthPublic.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${PUBLIC_API_TOKEN}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/* =====================================================================
 * MANAJEMEN SESI
 * ===================================================================== */

/**
 * Single-flight refresh.
 *
 * Semua request yang kena 401 pada waktu bersamaan menunggu SATU promise refresh
 * yang sama, bukan menembak endpoint refresh masing-masing.
 *
 * Tanpa ini, dua request paralel yang kedaluwarsa akan memanggil refresh
 * bersamaan dengan refreshToken yang sama. Backend merotasi refreshToken setiap
 * kali menerbitkan token baru, jadi refresh pertama membuat token yang dipakai
 * refresh kedua menjadi basi -> 401 -> cookie dihapus -> pengguna terlempar ke
 * login, padahal satu refresh sudah berhasil.
 */
let refreshPromise = null;

function getCookieOptions() {
  return {
    expires: 0.5,
    // `secure: true` membuat cookie TIDAK tersimpan di http://localhost, sehingga
    // token baru tidak pernah ter-update dan 401 berulang terus saat dev.
    secure: window.location.protocol === "https:",
    sameSite: "strict",
  };
}

function clearSessionAndRedirect() {
  lupakanPenandaPengumuman();
  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");
  Cookies.remove("sessionExpired");
  window.location.href = LOGIN_PATH;
}

async function requestNewToken(userId, refreshTokenValue) {
  return await axiosRefresh.post("/api/auth/refreshToken", { userId, refreshToken: refreshTokenValue }).then((res) => res.data);
}

async function refreshSession() {
  // Sudah ada refresh yang berjalan -> ikut menunggu yang itu.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const aToken = Cookies.get("accessToken");
    const rToken = Cookies.get("refreshToken");

    if (!aToken || !rToken) throw new Error("Sesi tidak tersedia");

    const decode = jwtDecode(aToken);
    const response = await requestNewToken(decode.userId, rToken);

    if (!response?.accessToken) throw new Error("Respons refresh tidak valid");

    const cookieOptions = getCookieOptions();
    Cookies.set("accessToken", response.accessToken, cookieOptions);
    Cookies.set("refreshToken", response.refreshToken, cookieOptions);
    Cookies.set("sessionExpired", response.sessionExpired, cookieOptions);

    return response.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    // Dilepas apa pun hasilnya, supaya 401 berikutnya bisa mencoba lagi.
    refreshPromise = null;
  }
}

axiosInstanceAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Normalkan message dari backend menjadi string SEBELUM error diteruskan.
    //
    // Menutup seluruh kelas crash "Objects are not valid as a React child":
    // banyak komponen menulis toast.error(err.response.data.message), sementara
    // backend kadang mengirim message berbentuk objek - baik dari validation pipe
    // ({ field: "pesan" }) maupun dari exception filter
    // ({ message, error, statusCode }). Dinormalkan sekali di sini, semua
    // komponen ikut aman tanpa perlu disentuh satu per satu.
    const data = error?.response?.data;
    if (data && typeof data === "object") {
      const flat = flattenMessage(data.message);
      if (flat) data.message = flat;
    }

    if (error.code === "ERR_NETWORK") {
      toast.error("Jaringan tidak tersedia");
      // reject, BUKAN return. Versi lama memakai `return toast.error(...)`
      // sehingga promise-nya RESOLVE dengan nilai berupa toast id - res.data
      // jadi undefined dan pemanggil menganggap request berhasil dengan data
      // kosong.
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    // Optional chaining: request yang dibatalkan atau kena CORS tidak punya
    // `response`, dan versi lama melempar TypeError di titik ini.
    const status = error.response?.status;

    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Satu request hanya diulang sekali. Tanpa penanda ini, request yang tetap
    // 401 setelah token baru akan diulang tanpa henti.
    if (originalRequest._retry) {
      clearSessionAndRedirect();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    try {
      const accessToken = await refreshSession();
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return axiosInstanceAuth(originalRequest);
    } catch (refreshError) {
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    }
  }
);

export async function fetchUserInfo(token) {
  // axiosInstance, bukan axios polos: versi lama tidak memakai baseURL sehingga
  // menembak origin frontend, bukan API. Gagal begitu frontend dan API berada
  // di host yang berbeda.
  const response = await axiosInstance.get("/api/users/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

export async function login(data) {
  return await axiosInstance
    .post("/api/auth/login", data)
    .then((res) => res)
    .catch((err) => err.response);
}

export async function registerUser(data) {
  return await axiosInstance
    .post("/api/auth/new-register", data)
    .then((res) => res)
    .catch((err) => err.response);
}

export async function forgotPassword(data) {
  return await axiosInstance
    .post("/api/auth/forgotPassword", data)
    .then((res) => res)
    .catch((err) => err.response);
}

export async function registerFromPTSP(data) {
  return await axiosInstance
    .post("/api/auth/user-from-ptsp", data)
    .then((res) => res)
    .catch((err) => err.response);
}

export async function resetPassword(data) {
  return await axiosInstance
    .post("/api/auth/resetPassword", data)
    .then((res) => res)
    .catch((err) => err.response);
}

export async function uploadPhoto(data) {
  return await axiosInstance
    .post("/api/upload/file", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res)
    .catch((err) => err.response);
}

export async function getListRegistrasi(data) {
  return await axiosInstanceAuth.get("/api/registrasi/search?page=1&limit=10&sortBy=createdAt&sortOrder=desc").then((res) => res.data);
}

export async function getDetailRegistrasi(data) {
  return await axiosInstanceAuth.get(`/api/registrasi/${data}`).then((res) => res.data);
}

export async function updatePerusahaan(id, data) {
  return await axiosInstanceAuth.put(`/api/registrasi/update-perusahaan/${id}`, data).then((res) => res.data);
}

export async function updateBahanB3(id, data) {
  return await axiosInstanceAuth.put(`/api/registrasi/${id}/update-bahan`, data).then((res) => res.data);
}

export async function submitSK(id) {
  return await axiosInstanceAuth.post(`/api/registrasi/submit-draft-sk/${id}`).then((res) => res.data);
}

export async function submitApprovalSK(id, data) {
  return await axiosInstanceAuth.put(`/api/registrasi/update-status-approval/${id}`, data).then((res) => res.data);
}

export async function simpanSK(id, data) {
  return await axiosInstanceAuth.post(`/api/registrasi/simpan-draft-sk/${id}`, data).then((res) => res.data);
}

export async function sendInsw(data) {
  return await axiosInstanceAuth.post(`/api/insw/send`, data).then((res) => res.data);
}

export async function sendInswPerubahan(data) {
  return await axiosInstanceAuth.post(`/api/insw/sendPerubahan`, data).then((res) => res.data);
}

export async function exportJsonINSW(data) {
  return await axiosInstanceAuth.post(`/api/insw/export-json`, data).then((res) => res.data);
}

export async function authStateFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => res.data);
}

export async function getFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => res.data);
}

export async function getFetcherPublic(url) {
  return await axiosInstanceAuthPublic.get(url).then((res) => res.data);
}

export async function getSelectFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.data?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getSelectProvinceFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getSelectFetcherPublic(url) {
  return await axiosInstanceAuthPublic.get(url).then((res) => {
    return res.data?.data?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getSelectFetcherPeriod(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getSelectFetcherJenisB3(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.data?.map((item) => ({
      value: item.id,
      label: item.casNumber && item.namaBahanKimia ? item.casNumber + " - " + item.namaBahanKimia : null,
    }));
  });
}

export async function getSelectB3SubstanceFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.b3Substances?.map((item) => ({
      value: item.id,
      label: `${item.dataBahanB3.casNumber} - ${item.dataBahanB3.namaDagang} - ${item.dataBahanB3.namaBahanKimia}`,
    }));
  });
}

export async function getBasicSelectFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data;
  });
}

export async function getSelectCountryFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res?.data?.map((item) => ({
      value: item.code2,
      label: item.name,
    }));
  });
}

export async function getSelectJenisSampleFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.map((item) => ({
      value: item.id,
      label: `${item.type} - ${item.deskripsi}`,
    }));
  });
}

export async function getSelectLocationsFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getSelectPejabatFetcher(url) {
  return await axiosInstanceAuth.get(url).then((res) => {
    return res.data?.data?.map((item) => ({
      value: item.id,
      label: item.nama && item.jabatan ? `${item.nama} - ${item.jabatan}` : item.nama ? item.nama : null,
    }));
  });
}

export async function postFetcher(url, data) {
  return await axiosInstanceAuth.post(url, data).then((res) => res.data);
}

export async function postMultipartFetcher(url, data) {
  return await axiosInstanceAuth
    .post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res);
}

export async function putFetcher(url, id, data) {
  return await axiosInstanceAuth.put(`${url}/${id}`, data).then((res) => res.data);
}

export async function putFetcherWithoutId(url, data) {
  return await axiosInstanceAuth.put(url, data).then((res) => res.data);
}

export async function patchFetcher(url, id, data) {
  return await axiosInstanceAuth.patch(`${url}/${id}`, data).then((res) => res.data);
}

export async function patchFetcherWithoutId(url, data) {
  return await axiosInstanceAuth.patch(url, data).then((res) => res.data);
}

export async function patchMultipartFetcher(url, data) {
  return await axiosInstanceAuth
    .patch(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res);
}

export async function deleteFetcher(url, id) {
  return await axiosInstanceAuth.delete(`${url}/${id}`).then((res) => res.data);
}

/**
 * @deprecated Meneruskan `data` sebagai config axios, bukan sebagai body -
 * jadi body-nya tidak pernah terkirim. Gunakan deleteWithFormFetcherTest().
 * Dibiarkan sementara agar pemakaian lama tidak langsung rusak.
 */
export async function deleteWithFormFetcher(url, data) {
  return await axiosInstanceAuth.delete(url, { data }).then((res) => res.data);
}

export async function postWithFormDataFetcher(url, data) {
  return await axiosInstanceAuth
    .post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res);
}

export async function putWithFormDataFetcher(url, id, data) {
  return await axiosInstanceAuth
    .put(`${url}/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res);
}

export async function deleteWithFormFetcherTest(url, data) {
  return await axiosInstanceAuth
    .delete(url, {
      data: data, // Mengirim data sebagai body
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res.data);
}

export async function getPdfUrl(url) {
  try {
    const response = await axiosInstanceAuth.get(url, {
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];
    let filename = "download.pdf";

    if (contentDisposition) {
      console.log("Content disposition:", contentDisposition);
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }

    console.log("Extracted filename:", filename);

    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // UBAH BAGIAN INI - Tambahkan filename ke fragment
    window.open(`${pdfUrl}#filename=${encodeURIComponent(filename)}`, "_blank");

    // Cleanup setelah beberapa waktu
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
  } catch (error) {
    console.error("Failed to open PDF:", error);
    throw new Error("Failed to open PDF");
  }
}

// Fungsi untuk download langsung dengan nama benar
export async function downloadPdfWithName(url) {
  try {
    const response = await axiosInstanceAuth.get(url, {
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];
    let filename = "surat-registrasi-b3.pdf";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }

    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
  } catch (error) {
    console.error("Failed to download PDF:", error);
    throw new Error("Failed to download PDF");
  }
}

export async function toggleFavoriteCategory(id, data) {
  return await axiosInstanceAuth.post(`/api/content/category/${id}/favorite`, data).then((res) => res.data);
}

export async function postCategory(data) {
  return await axiosInstanceAuth.post(`/api/content/category`, data).then((res) => res.data);
}

export async function getFetcherDashoard(url) {
  return await axiosInstanceAuthPublic.get(url).then((res) => res.data);
}

export async function getGeoJSON(url) {
  return await axiosInstanceAuth.get(url).then((res) => res.data);
}

export async function openDocumentOrFile(url, responseType = "blob", fileType = "application/octet-stream") {
  try {
    const response = await axiosInstanceAuth.get(url, {
      responseType: responseType, // Bisa disesuaikan untuk jenis file lain
    });

    // Membuat URL dari data Blob
    const fileBlob = new Blob([response.data], { type: fileType });
    const fileUrl = URL.createObjectURL(fileBlob);

    // Membuka file di tab baru
    window.open(fileUrl, "_blank");

    // Opsional: Membersihkan URL setelah waktu tertentu
    setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
  } catch (error) {
    console.error("Gagal membuka file:", error);
    throw new Error("Gagal membuka file");
  }
}

export async function getFetcherProvinceJariEmas(url) {
  return await axiosInstanceAuthJariEmas.get(url).then((res) => {
    return res.data?.data?.province?.map((item) => ({
      value: item.id,
      label: item.name ? item.name : item.namaDagang ? item.namaDagang : item.nama ? item.nama : null,
    }));
  });
}

export async function getFetcherJariEmas(url) {
  return await axiosInstanceAuthJariEmas.get(url).then((res) => res.data);
}

export async function postFetcherMotivasiMerkuri(url, data) {
  return await axiosInstanceAuthMotivasiMerkuri.post(url, data).then((res) => res.data);
}

export async function downloadFetcher(fileUrl, fileName) {
  return await axiosInstanceAuth
    .get(fileUrl, {
      responseType: "blob",
      headers: {
        "Content-Type": "application/pdf",
      },
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    });
}

export async function updateCompany(id, data) {
  return await axiosInstanceAuth.put(`/api/company/update-company/${id}`, data).then((res) => res.data);
}

export async function selectCompany(companyId) {
  return await axiosInstanceAuth.post(`/api/company/select-company/${companyId}`).then((res) => res.data);
}
